import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { ApolloCache } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  Portal_GetPortalByIdDocument,
  Portal_GetPortalByIdQuery,
  Portal_UpdatePortalNameByIdInput,
  usePortal_GetPortalByIdQuery,
  usePortal_UpdatePortalNameByIdMutation,
} from 'queries';
import * as yup from 'yup';

import FormTextField from 'common/form-text-field';
import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

type Props = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  onExitClick: Function;
};

const useStyles = makeStyles(() => ({
  h5: {
    paddingTop: '5px',
  },
  formSection: {
    paddingBottom: '10px',
  },
  form: {
    textAlign: 'left',
  },
  inputField: {
    width: '100%',
  },
  inputGrid: {
    paddingRight: '20px',
  },
  inputError: {
    backgroundColor: '#FFF1F1',
    color: 'red',
  },
  container: {
    paddingTop: '10px',
  },
  warning: {
    display: 'block',
    color: 'red',
    fontSize: '.65rem',
    lineHeight: '1.3',
  },
}));

const schema = yup.object().shape({
  name: yup.string().required('Please enter a portal name'),
});

export default function ChangePortalNameForm({ onExitClick }: Props) {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const { data: portalData, loading: portalLoading } = usePortal_GetPortalByIdQuery({
    variables: { id: portalId },
    onError: (e) => {
      showAlert({
        message: 'Error getting portal by ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    watch,
  } = useForm<Portal_UpdatePortalNameByIdInput>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: yupResolver(schema),
    values: {
      name: portalData?.data?.name || '',
      portalId,
    },
  });

  const readPortalByIdQuery = (
    store: ApolloCache<Portal_GetPortalByIdQuery>,
  ): Portal_GetPortalByIdQuery | null => {
    return store.readQuery<Portal_GetPortalByIdQuery>({
      query: Portal_GetPortalByIdDocument,
      variables: {
        id: portalId,
      },
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const writePortalByIdQuery = (store: ApolloCache<Portal_GetPortalByIdQuery>, portal: any) => {
    store.writeQuery<Portal_GetPortalByIdQuery>({
      query: Portal_GetPortalByIdDocument,
      variables: {
        id: portalId,
      },
      data: {
        data: portal,
      },
    });
  };

  const [updatePortalName, { loading: updatePortalNameExecuting }] =
    usePortal_UpdatePortalNameByIdMutation({
      update: (store, { data }) => {
        const portalData = readPortalByIdQuery(store);
        if (portalData) {
          writePortalByIdQuery(store, data!.data);
          showToast({
            message: 'Portal name was updated successfully',
            severity: NotificationSeverity.SUCCESS,
          });
          onExitClick();
        }
      },
      onError: (e) =>
        showAlert({
          message: `Error updating portal name: ${e.message}`,
          severity: NotificationSeverity.ERROR,
        }),
    });

  if (!portalData && portalLoading) {
    return <Spinner />;
  }

  const portal = portalData!.data;

  const submitForm = async (formData: Portal_UpdatePortalNameByIdInput) => {
    // updating a portal name
    updatePortalName({
      variables: {
        input: {
          name: formData.name,
          portalId,
        },
      },
    });
  };

  const name = watch('name');

  return (
    <>
      <Typography variant="h5" className={classes.h5}>
        Change Portal Name
      </Typography>
      <Typography>Current Name: {portal?.name}</Typography>
      <form
        id="change-portal-name-form"
        className={classes.form}
        onSubmit={handleSubmit(submitForm)}
      >
        <div className={classes.formSection}>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={12}>
              <Controller
                name="name"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    errorMessage={errors.name?.message}
                    label="New Portal Name"
                    className={classes.inputField}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={8}>
              <Button
                disabled={!name || isSubmitting || updatePortalNameExecuting}
                type="submit"
                form="change-portal-name-form"
                variant="contained"
                color="primary"
                style={{ marginLeft: '10px' }}
              >
                Rename Portal
              </Button>
            </Grid>
          </Grid>
        </div>
      </form>
    </>
  );
}
